'use client';
import { useEffect, useState, useCallback } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useT } from '@/lib/useT';
import toast from 'react-hot-toast';

interface OrgEntry {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  brandColor: string | null;
  plan: string;
  role: string;
}

/**
 * Org switcher dropdown. Shown only when the user belongs to ≥2 active orgs.
 * Calls /auth/switch-org which re-issues access + refresh tokens scoped to
 * the new orgId, then reloads to reset all client state.
 */
export function OrgSwitcher() {
  const t = useT();
  const [orgs, setOrgs] = useState<OrgEntry[] | null>(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const user = useAuthStore(s => s.user);
  const setSession = useAuthStore(s => (s as any).setSession);

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/auth/my-orgs') as OrgEntry[];
      setOrgs(r);
    } catch { setOrgs([]); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!orgs || orgs.length < 2) return null;
  const current = orgs.find(o => o.id === (user as any)?.orgId) || orgs[0];

  const switchTo = async (orgId: string) => {
    if (orgId === current?.id) { setOpen(false); return; }
    setSwitching(orgId);
    try {
      const r = await api.post('/auth/switch-org', { orgId }) as any;
      // Update tokens in the store. The store may expose either setSession
      // or individual setters depending on revision.
      if (typeof setSession === 'function') {
        setSession({ accessToken: r.accessToken, refreshToken: r.refreshToken, user: r.user });
      }
      toast.success(`→ ${orgs.find(o => o.id === orgId)?.name || ''}`);
      // Hard reload — reset all in-memory state (queries, websocket, etc.)
      window.location.reload();
    } catch (e: any) {
      toast.error(e.message || t('osw_switch_failed'));
      setSwitching(null);
    }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
        style={{ background: 'var(--c-panel)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}>
        {current?.logo
          ? <img src={current.logo} alt="" className="w-4 h-4 rounded" />
          : <Building2 size={12} style={{ color: current?.brandColor || 'var(--c-accent)' }} />}
        <span className="font-semibold max-w-[140px] truncate">{current?.name}</span>
        <ChevronDown size={11} style={{ color: 'var(--c-muted)' }} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-40 w-72 rounded-xl py-1 overflow-hidden"
            style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
            {orgs.map(o => {
              const active = o.id === current?.id;
              return (
                <button key={o.id} onClick={() => switchTo(o.id)} disabled={!!switching}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50">
                  {o.logo
                    ? <img src={o.logo} alt="" className="w-7 h-7 rounded" />
                    : <div className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                        style={{ background: o.brandColor || '#FF652F' }}>
                        {o.name[0]?.toUpperCase()}
                      </div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--c-text)' }}>{o.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--c-muted)' }}>
                      {o.role} · {o.plan}
                    </p>
                  </div>
                  {active && <span className="text-[10px] font-bold" style={{ color: 'var(--c-accent)' }}>●</span>}
                  {switching === o.id && <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>…</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
